import { ModaSofa } from '@dora/backpack';
import * as Selectors from '../../../store/selectors';
import { useSelector } from '../../hooks/redux';
import { interstitialPlacements } from './useSofaConfigure/interstitialPlacements';
import { SofaConfigureSeatCount } from './useSofaConfigure/numberOfSeatsFromSize';
import { SeatSize } from './useSofaConfigure/seatSizes';
import * as H from 'preact/hooks';

const buildSize =
    (seatsPerEndPieceL: number, seatsPerEndPieceR: number, seatsPerStraightPiece: number) =>
    (
        shape: ModaSofa.Shape,
        seats: number,
        rightSeats: number,
        leftSeats: number,
        middleSeats: number,
    ): ModaSofa.Size.Size => {
        if (seatsPerStraightPiece !== 1) {
            /** TODO: handle case where straight seats per piece is not one if it ever comes up */
            throw new Error(
                'a straight sofa piece does not have a seat size of one - this is not currently supported',
            );
        }
        // TODO: consider how to construct this size type such that it can't be incorrect
        if (shape === 'straight') {
            return { shape, middle: seats - seatsPerEndPieceL - seatsPerEndPieceR };
        } else if (shape === 'corner') {
            return {
                shape,
                left: leftSeats - seatsPerEndPieceL,
                right: rightSeats - seatsPerEndPieceR,
            };
        } else {
            return {
                shape,
                left: leftSeats - seatsPerEndPieceL,
                right: rightSeats - seatsPerEndPieceR,
                middle: middleSeats,
            };
        }
    };

/**
 * Convert our form state into a full sofa configuration.
 */

export function useFormStateToBuildConfiguration(
    seatSize: SeatSize,
    shape: ModaSofa.Shape,
    seatCount: SofaConfigureSeatCount,
    cornerStyle: ModaSofa.CornerStyleT,
    interstitialArms: { left: number; right: number; middle: number },
) {
    const configurator = useSelector(Selectors.ModaSofa.currentConfigurator);

    if (configurator === null) {
        return;
    }

    // The following few derived values are heavily memoised to prevent the Form from constantly updating.
    // This was causing some performance degridations and, in some cases, infinite loops
    const size = H.useMemo(() => {
        return buildSize(seatSize.endPieceLeft, seatSize.endPieceRight, seatSize.middlePiece)(
            shape,
            seatCount.straightSeats,
            seatCount.rightSeats,
            seatCount.leftSeats,
            seatCount.middleSeats,
        );
    }, [
        shape,
        seatCount.leftSeats,
        seatCount.middleSeats,
        seatCount.rightSeats,
        seatCount.straightSeats,
    ]);

    const interstitialPositions = H.useMemo(() => {
        return interstitialPlacements(interstitialArms, size);
    }, [interstitialArms.left, interstitialArms.right, interstitialArms.middle, size]);

    const configuration = H.useMemo(() => {
        return configurator(
            size,
            { 'drinks-cooler-armrest': { placements: interstitialPositions } },
            cornerStyle,
        );
    }, [size, cornerStyle, interstitialPositions]);

    return { configuration };
}
